/**
 * Componente de navegação da aplicação
 * Navbar superior e inferior para navegação
 */
import { LitElement, html, css } from 'lit'
import { authService } from '../core/services/auth.service.js'
import { premiumService } from '../core/services/premium.service.js'

export class Navbar extends LitElement {
  static properties = {
    isPremium: { type: Boolean },
    premiumLoading: { type: Boolean }
  }

  navItems = [
    { href: '#/dashboard', label: 'Início', icon: 'fa-solid fa-house', premium: false },
    { href: '#/transacoes', label: 'Transações', icon: 'fa-solid fa-receipt', premium: true },
    { href: '#/orcamento', label: 'Orçamento', icon: 'fa-solid fa-piggy-bank', premium: true },
    { href: '#/relatorios', label: 'Relatórios', icon: 'fa-solid fa-chart-bar', premium: true },
    { href: '#/configuracoes', label: 'Configurações', icon: 'fa-solid fa-gear', premium: true }
  ]

  constructor() {
    super()
    this.isPremium = false
    this.premiumLoading = true
  }

  createRenderRoot() {
    return this
  }

  async connectedCallback() {
    super.connectedCallback()
    window.addEventListener('hashchange', () => this.requestUpdate())
    await this.checkPremiumStatus()
  }

  /**
   * Verifica status premium (com cache)
   */
  async checkPremiumStatus() {
    try {
      this.isPremium = await premiumService.isPremium()
    } catch (error) {
      console.error('Erro ao verificar premium no navbar:', error)
      this.isPremium = false
    } finally {
      this.premiumLoading = false
    }
  }

  /**
   * Verifica se o item de navegação está ativo comparando com o hash atual da URL.
   * Normaliza variantes de hash que representam a mesma rota:
   * - Hash vazio ("") → "#/dashboard" (carregamento inicial sem hash)
   * - "#/" → "#/dashboard" (rota raiz explícita)
   * - "#dashboard" → "#/dashboard" (redirecionamento do login sem barra)
   * @param {string} href - Hash do item de navegação (ex: '#/dashboard', '#/transacoes')
   * @returns {boolean} true se o item corresponde à rota atual
   */
  isActive(href) {
    const currentHash = window.location.hash || '#/dashboard'
    const normalizedHash = currentHash === '#/' || currentHash === '#dashboard'
      ? '#/dashboard'
      : currentHash
    return normalizedHash === href
  }

  async handleLogout() {
    // Limpa cache premium no logout
    premiumService.clearCache()
    await authService.logout()
  }

  /**
   * Verifica se item do menu deve estar habilitado
   */
  isItemEnabled(item) {
    // Se item não é premium, sempre habilitado
    if (!item.premium) return true
    // Se item é premium, só habilita se usuário é premium
    return this.isPremium
  }

  /**
   * Verifica se deve mostrar badge PRO
   */
  shouldShowProBadge(item) {
    return item.premium && !this.isPremium
  }

  render() {
    return html`
      <style>
        .premium-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          background: var(--cta-primary, #FF8A3D);
          color: var(--text-primary, #F4F7FF);
          font-size: 8px;
          padding: 1px 3px;
          border-radius: 3px;
          font-weight: bold;
        }

        .premium-badge:hover {
          background: var(--cta-hover, #F97316);
        }
        
        .nav-item-disabled {
          opacity: 0.5;
          pointer-events: none;
        }
      </style>
      
      <nav class="bottom-navbar" role="navigation" data-cy="bottom-navbar">
        ${this.navItems.map(item => html`
          <a href="${item.href}"
             class="bottom-item ${this.isActive(item.href) ? 'is-active' : ''} ${!this.isItemEnabled(item) ? 'nav-item-disabled' : ''}"
             data-cy="nav-${item.href.replace('#/', '')}"
             @click="${(e) => this.handleNavClick(e, item)}">
            <div style="position: relative;">
              <i class="${item.icon}"></i>
              ${this.shouldShowProBadge(item) ? html`<span class="premium-badge">PRO</span>` : ''}
            </div>
            <span>${item.label}</span>
          </a>
        `)}
      </nav>
    `
  }

  /**
   * Manipula clique na navegação
   */
  handleNavClick(e, item) {
    if (item.premium && !this.isPremium) {
      e.preventDefault()
      this.dispatchEvent(new CustomEvent('show-upgrade-modal', {
        bubbles: true,
        composed: true
      }))
    }
  }
}

customElements.define('nav-bar', Navbar)